import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import EventRounded from '@mui/icons-material/EventRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { FaixasTurma } from '@/components/graficos/FaixasTurma';
import { fmt, situacao } from '@/features/notas/calculo';
import { SeletorSemestre } from '@/features/notas/SeletorSemestre';
import { CartaoOfertas } from '@/features/notas/Ofertas';
import {
  AvisoVendoComo,
  CarregandoPainel,
  CartaoInsight,
  ChipSituacaoP1,
  Destaque,
} from '@/features/notas/componentes';
import { insightsDisciplina } from '@/features/notas/insights';
import { dataBr, diasAte, primeiroNome } from '@/features/notas/texto';
import { rotuloSemestre, type DisciplinaDesempenho } from '@/features/notas/types';
import { useDesempenho } from '@/features/notas/useDesempenho';
import { PageHeader } from './PageHeader';

export function PainelPage() {
  const { ra } = useParams();
  const [params, setParams] = useSearchParams();
  const { data, error, loading } = useDesempenho(ra, params.get('semestre') ?? undefined);
  const base = ra ? `/admin/aluno/${ra}` : '';

  if (loading && !data) return <CarregandoPainel />;
  if (error || !data)
    return (
      <Alert severity="error">
        Não consegui carregar as suas notas agora. Recarregue a página.
      </Alert>
    );

  const discs = data.disciplinas;
  if (!data.semestre) {
    return (
      <Alert severity="info">
        Ainda não há notas publicadas para você. Assim que o professor publicar, elas aparecem aqui.
      </Alert>
    );
  }
  const encerrado = discs.length > 0 && discs.every((d) => d.somenteNotas);
  const p1s = discs.map((d) => d.notas.P1).filter((v): v is number => typeof v === 'number');
  const mediaP1 = p1s.length ? p1s.reduce((s, x) => s + x, 0) / p1s.length : null;
  const pcts = discs
    .map((d) => d.turmaInfo.componentes.P1?.percentil)
    .filter((v): v is number => typeof v === 'number');
  const pctMedio = pcts.length ? pcts.reduce((s, x) => s + x, 0) / pcts.length : null;
  const t2 = data.datas.T2;
  const diasT2 = t2 ? diasAte(t2) : null;
  const destaques = encerrado
    ? []
    : discs.flatMap((d) =>
        insightsDisciplina(d)
          .slice(0, 2)
          .map((i) => ({ ...i, disc: d.codigo })),
      );
  const sufixo = `?semestre=${data.semestre}`;

  return (
    <>
      {ra && <AvisoVendoComo nome={data.aluno.nome} />}
      <PageHeader
        title={`Olá, ${primeiroNome(data.aluno.nome)}!`}
        subtitle={`RA ${data.aluno.ra} · semestre ${rotuloSemestre(data.semestre)} · notas atualizadas em ${dataBr(data.atualizadoEm, true)}`}
        actions={
          <SeletorSemestre
            semestres={data.semestres}
            valor={data.semestre}
            onChange={(s) => setParams({ semestre: s })}
          />
        }
      />

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Destaque
            icone={<SchoolRounded fontSize="small" />}
            rotulo="Disciplinas comigo"
            valor={discs.length}
            rodape={discs.map((d) => d.codigo).join(' · ')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Destaque
            icone={<InsightsRounded fontSize="small" />}
            rotulo="Média das suas P1"
            valor={fmt(mediaP1, 1)}
            rodape={p1s.length < discs.length ? 'algumas P1 ainda pendentes' : 'de 0 a 10'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Destaque
            icone={<InsightsRounded fontSize="small" />}
            rotulo="Sua posição média"
            valor={pctMedio === null ? '–' : `${Math.round(pctMedio)}%`}
            rodape="da turma ficou abaixo de você na P1"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {encerrado ? (
            <Destaque
              icone={<EventRounded fontSize="small" />}
              rotulo="Semestre"
              valor={rotuloSemestre(data.semestre)}
              rodape="encerrado"
            />
          ) : (
            <Destaque
              icone={<EventRounded fontSize="small" />}
              rotulo="Prazo da T2 (AWS)"
              valor={diasT2 === null ? '–' : diasT2 > 0 ? `${diasT2} dias` : 'encerrado'}
              rodape={t2 ? `até ${dataBr(t2)}, 23h59` : undefined}
            />
          )}
        </Grid>
      </Grid>

      <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
        Suas disciplinas
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {discs.map((d) => (
          <Grid key={d.codigo} size={{ xs: 12, md: discs.length > 1 ? 6 : 12 }}>
            <CartaoDisciplina d={d} to={`${base}/disciplina/${d.codigo}${sufixo}`} />
          </Grid>
        ))}
      </Grid>

      {discs.some((d) => d.turmaInfo.componentes.P1) && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" component="h2">
              Sua P1 em cada turma
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              O ponto é você. A faixa cinza mostra onde ficou a metade do meio da turma, e o traço,
              a mediana.
            </Typography>
            <FaixasTurma
              linhas={discs
                .filter((d) => d.turmaInfo.componentes.P1)
                .map((d) => ({
                  rotulo: `${d.codigo} · ${d.periodo}`,
                  estat: d.turmaInfo.componentes.P1!,
                }))}
            />
          </CardContent>
        </Card>
      )}

      {destaques.length > 0 && (
        <>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Para você reparar
          </Typography>
          <Grid container spacing={2}>
            {destaques.map((i, k) => (
              <Grid key={k} size={{ xs: 12, sm: 6, md: 4 }}>
                <CartaoInsight
                  insight={{
                    ...i,
                    titulo: discs.length > 1 ? `${i.disc} · ${i.titulo}` : i.titulo,
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
      {!ra && (
        <Box sx={{ mt: 4 }}>
          <CartaoOfertas />
        </Box>
      )}
    </>
  );
}

function CartaoDisciplina({ d, to }: { d: DisciplinaDesempenho; to: string }) {
  const p1 = d.notas.P1;
  const estat = d.turmaInfo.componentes.P1;
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea component={RouterLink} to={to} sx={{ height: '100%' }}>
        <CardContent>
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary">
                {d.codigo} · {d.turma} · {d.periodo}
              </Typography>
              <Typography variant="h6" component="h3" sx={{ lineHeight: 1.25 }}>
                {d.nome}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                P1
              </Typography>
              <Typography
                variant="h3"
                component="p"
                sx={{ lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
              >
                {fmt(p1, 1)}
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ mt: 2, flexWrap: 'wrap', alignItems: 'center' }}
          >
            {d.somenteNotas ? <ChipFinal d={d} /> : <ChipSituacaoP1 p1={p1} />}
            {estat && (
              <Typography variant="body2" color="text.secondary">
                média da turma {fmt(estat.media)}
                {estat.percentil !== null && ` · acima de ${Math.round(estat.percentil)}% dela`}
              </Typography>
            )}
          </Stack>
          {d.pendencias.length > 0 && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              Ainda pendente: {d.pendencias.join(', ')}
            </Typography>
          )}
          <Button component="span" endIcon={<ArrowForwardRounded />} sx={{ mt: 1.5, ml: -1 }}>
            Ver detalhes
          </Button>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/** Semestre encerrado: P1, P2, P3 e a situação pela regra do plano de ensino. */
function ChipFinal({ d }: { d: DisciplinaDesempenho }) {
  const sit = situacao({ P1: d.notas.P1, P2: d.notas.P2, P3: d.notas.P3 || null });
  const rotulo =
    sit.tipo === 'aprovado'
      ? `Média ${fmt(sit.mf, 1)}: aprovado`
      : sit.tipo === 'p3'
        ? `Média ${fmt(sit.mf, 1)} sem P3`
        : `Média ${fmt(sit.mf, 1)}`;
  return (
    <>
      <Chip
        size="small"
        variant="outlined"
        color={sit.tipo === 'aprovado' ? 'success' : 'default'}
        label={rotulo}
      />
      <Typography variant="body2" color="text.secondary">
        P1 {fmt(d.notas.P1, 1)} · P2 {fmt(d.notas.P2, 1)} · P3 {fmt(d.notas.P3, 1)}
      </Typography>
    </>
  );
}
